import {
    IonButton,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
} from '@ionic/react';
import { Plural, Trans, t } from '@lingui/macro';
import { useAtom } from 'jotai';
import React, { useCallback, useMemo } from 'react';

import LoadableContent from '../../../components/content/LoadableContent';
import InterventionPage from '../../../components/page/InterventionPage';
import { IRoomWithDevices } from '../../../interfaces/IOdooRoomsResponse';
import { IInstallationRouteProps } from '../../../interfaces/IRoute';
import {
    photosListLoadableAtom,
    roomsWithDevicesAndStateAtom,
    skipLteDboxAtom,
    tabsEnabledAtom,
    updateDBStateAtom,
} from '../../../store/Atoms';
import { LoadableValue } from '../../../types/Generic';
import { ImageCategories } from '../../../types/ImageInfo';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';

const Overview: React.FC<IInstallationRouteProps> = ({ match }) => {
    const jobId = match.params.jobId;
    const router = useCustomRouter();
    const [, setTabsEnabled] = useAtom(tabsEnabledAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [skipLteDbox] = useAtom(skipLteDboxAtom);
    const [value] = useAtom(roomsWithDevicesAndStateAtom);
    const [photosValue] = useAtom(photosListLoadableAtom);
    const combinedValue: LoadableValue = {
        state:
            value.state === 'loading' || photosValue.state === 'loading'
                ? 'loading'
                : value.state === 'hasError' ||
                    photosValue.state === 'hasError'
                  ? 'hasError'
                  : 'hasData',
    };
    const rooms = value.state === 'hasData' ? value.data : [];
    const hasLteDbox = rooms.some((room) =>
        room.devices.some((device) => device.hasLte)
    );
    const interventionImages: string[] = useMemo(
        () => (photosValue.state === 'hasData' ? photosValue.data : []),
        [photosValue]
    );
    const photosUploaded = rooms.every((room) =>
        room.devices
            .filter((device) => device.isNew)
            .every((device) => {
                return (
                    interventionImages.some((image) =>
                        image.includes(
                            `${ImageCategories.heaterDBOXWiring}_${device.macAddress}`
                        )
                    ) &&
                    interventionImages.some((image) =>
                        image.includes(
                            `${ImageCategories.heaterPhotoHeaterLabel}_${device.macAddress}`
                        )
                    )
                );
            })
    );
    const allDevicesInfoAreCompleted = rooms.every((room) =>
        room.devices.every(
            (device) =>
                device.heaterLocation &&
                device.underWindow &&
                device.wallSurface &&
                device.dboxHeaterRelativeLocation &&
                device.dboxAirFlowQuality &&
                device.location &&
                device.dboxCloseToHeater &&
                device.holderPosition
        )
    );
    const allRoomsHaveDevices =
        !!rooms.length && rooms.every((room) => room.devices.length > 0);
    const canProceed =
        (skipLteDbox || hasLteDbox) && photosUploaded && allRoomsHaveDevices;

    const onProceedHandler = useCallback(() => {
        setTabsEnabled((prevState) => {
            return { ...prevState, mesh: true };
        });
        updateDBState();
        router.push(`/${jobId}/installation/mesh`, 'none');
    }, [jobId, router, setTabsEnabled, updateDBState]);

    return (
        <InterventionPage title={t`Wire`}>
            <LoadableContent value={combinedValue}>
                <IonList>
                    {(!(skipLteDbox || hasLteDbox) ||
                        !photosUploaded ||
                        !allRoomsHaveDevices) && (
                        <IonListHeader color="warning">
                            <IonLabel>
                                {!(skipLteDbox || hasLteDbox) && (
                                    <h6>
                                        <Trans>
                                            Please make sure to install 1 D-Box
                                            with LTE
                                        </Trans>
                                    </h6>
                                )}
                                {!allRoomsHaveDevices && (
                                    <h6>
                                        {rooms.length === 0 ? (
                                            <Trans>
                                                You need to create at least one
                                                room
                                            </Trans>
                                        ) : (
                                            <Trans>
                                                Some rooms are missing devices
                                            </Trans>
                                        )}
                                    </h6>
                                )}
                                {!photosUploaded && (
                                    <h6>
                                        <Trans>
                                            Some devices are missing photos
                                        </Trans>
                                    </h6>
                                )}
                                {!allDevicesInfoAreCompleted && (
                                    <h6>
                                        <Trans>
                                            Some devices are missing
                                            information
                                        </Trans>
                                    </h6>
                                )}
                            </IonLabel>
                        </IonListHeader>
                    )}
                    <div className="ion-padding">
                        <IonButton
                            expand="block"
                            routerLink={`/${jobId}/installation/wire/rooms`}
                            routerDirection="forward"
                        >
                            <Trans>Add Room</Trans>
                        </IonButton>
                        <IonButton
                            expand="block"
                            onClick={onProceedHandler}
                            disabled={!canProceed}
                            className="ion-margin-top"
                        >
                            <Trans>Proceed</Trans>
                        </IonButton>
                    </div>
                    {rooms.map((room: IRoomWithDevices) => {
                        return (
                            <div key={room.roomId}>
                                <IonItem
                                    detail
                                    key={room.roomId}
                                    routerLink={`/${jobId}/installation/wire/rooms/${room.roomId}`}
                                    routerDirection="forward"
                                >
                                    <IonLabel>
                                        <h2>{room.name}</h2>
                                        <p>
                                            <Plural
                                                value={room.devices.length}
                                                one="# heater"
                                                other="# heaters"
                                            />
                                        </p>
                                    </IonLabel>
                                </IonItem>
                            </div>
                        );
                    })}
                </IonList>
            </LoadableContent>
        </InterventionPage>
    );
};

export default Overview;
